import {
  eachDayOfInterval,
  format,
  isDate,
  isSameDay,
  isSameMonth,
  isValid,
  lastDayOfMonth,
} from "date-fns";
import { debounce, minBy, sum } from "lodash";
import React, {
  createRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Button } from "reactstrap";
import { TimesheetEntry } from "../../core/types";
import { Either, error, result } from "../../utils/fp/either";
import DateHeader from "../DateHeader";
import TimesheetEntryTable from "../TimesheetEntryTable";
import styled from "./Pane.styles";

interface PaneProps {
  timesheetEntries: Record<string, TimesheetEntry[]>;
  selectedDate?: Date;
  listenToScroll: boolean;
  onEditEntry: (entry: TimesheetEntry) => void;
  onSelectDay: (d: Date) => void;
  onCreate: (d: Date) => void;
  onRemoveAll: (entries: TimesheetEntry[]) => void;
}

interface CalendarMonth {
  month: number;
  year: number;
}

const scrollToDateAnchorFormat = "yyyyMMdd";

const Pane: React.FC<PaneProps> = (props) => {
  const {
    listenToScroll,
    timesheetEntries,
    onEditEntry: editEntry,
    onSelectDay: selectDay,
    selectedDate,
    onCreate,
    onRemoveAll,
  } = props;

  const containerRef = React.useRef<HTMLDivElement>(null);

  const [selectedDateAnchor, setSelectedDateAnchor] = useState("");
  const [scrolledDate, setScrolledDate] = useState<Date | null>(null);

  useLayoutEffect(() => {
    if (selectedDate) {
      setSelectedDateAnchor(format(selectedDate, scrollToDateAnchorFormat));
    }
    if (scrolledDate === null || !isSameDay(scrolledDate!, selectedDate!)) {
      scrollToDate(selectedDate, !isSameMonth(scrolledDate!, selectedDate!));
    }
  }, [selectedDate]);

  const scrollListener = debounce(
    (elements: HTMLDivElement[], days: Date[]) => {
      const deltas = elements.map(
        (element) =>
          [
            element,
            isInViewport(element, containerRef.current as HTMLElement),
          ] as [HTMLDivElement, number]
      );
      const [elementInView] = minBy(deltas, ([element, delta]) =>
        Math.abs(delta)
      )!;

      const index = elements.indexOf(elementInView as HTMLDivElement);

      if (elementInView) {
        setScrolledDate(days[index]);
        selectDay(days[index]);
      }
    },
    60
  );

  const scrollToDate = (date?: Date, immediate?: boolean) => {
    if (!date) {
      return;
    }

    if (!isValid(date) || !isDate(date)) {
      return;
    }

    const element = document.getElementById(
      format(date, scrollToDateAnchorFormat)
    );

    const delta =
      element?.getBoundingClientRect().top! - containerRef.current?.offsetTop!;

    window.scrollBy({
      top: delta!,
      behavior: immediate ? undefined : "smooth",
    });

    setScrolledDate(date);
  };

  const row = calendarMonthToDate({
    month: selectedDate!.getMonth(),
    year: selectedDate!.getFullYear(),
  })
    .map(
      (start) => {
        const interval: Interval = {
          start: start,
          end: lastDayOfMonth(start),
        };
        const days = eachDayOfInterval(interval);

        const elementRefs = useRef(days.map(() => createRef<HTMLDivElement>()));

        const scrollHandler = () =>
          scrollListener(
            elementRefs.current.map((r) => r.current!),
            days
          );

        useEffect(() => {
          if (!listenToScroll) {
            return;
          }

          window.addEventListener("scroll", scrollHandler);
          return () => window.removeEventListener("scroll", scrollHandler);
        }, [selectedDate?.getMonth(), listenToScroll]);

        {
          return (
            <styled.Container
              className="days"
              style={{
                position: "relative",
              }}
              ref={containerRef}
            >
              {days.map((date, i) => {
                const anchor = format(date, scrollToDateAnchorFormat);
                let dayClass =
                  anchor === selectedDateAnchor ? "day selected" : "day";

                if (timesheetEntries[date.toISOString()] === undefined)
                  dayClass += " empty";

                return (
                  <div
                    onClick={(ev) => scrollToDate(date)}
                    id={anchor}
                    className={dayClass}
                    key={date.toISOString()}
                    ref={elementRefs.current[i]}
                  >
                    <DateHeader
                      active={anchor === selectedDateAnchor}
                      date={date}
                      duration={sum(
                        (timesheetEntries[date.toISOString()] || []).map(
                          (e) => e.duration
                        )
                      )}
                      deleteAllEntries={() => {
                        if (!confirm("Alle Einträge wirklich entfernen?")) {
                          return;
                        }
                        onRemoveAll(timesheetEntries[date.toISOString()]);
                      }}
                    />
                    <TimesheetEntryTable
                      key={`table-${start.toISOString()}`}
                      editEvent={(entry) => editEntry(entry)}
                      entries={timesheetEntries[date.toISOString()] || []}
                    />
                    <div
                      style={{ textAlign: "center" }}
                      className="createButton"
                    >
                      <Button
                        color="primary"
                        size="sm"
                        outline
                        style={{
                          borderRadius: "1rem",
                          marginTop: "1rem",
                          marginBottom: "0.5rem",
                        }}
                        onClick={() => onCreate(date)}
                      >
                        Eintrag hinzufügen
                      </Button>
                    </div>
                  </div>
                );
              })}
            </styled.Container>
          );
        }
      },
      (err) => <div>{err}</div>
    )
    .value();

  return row ? row : <div>Empty</div>;
};

const calendarMonthToDate = (
  calendarMonth: CalendarMonth
): Either<string, Date> => {
  const date = new Date(calendarMonth.year, calendarMonth.month);
  if (isValid(date)) return result(date);
  return error(
    `'calendarMonth' is an invalid date. Input: Month: ${calendarMonth.month}; Year: ${calendarMonth.year}`
  );
};

const isInViewport = (elem: HTMLElement | null, root: HTMLElement | null) => {
  if (!elem || root === null) return 0;
  var bounding = elem.getBoundingClientRect();

  const delta = bounding.top - root.offsetTop;
  return delta;
};

export default Pane;
